import { FileMetaData } from "@/models/File"
import { toDate } from "@/utils/dateUtil"

import { Feature } from "./Feature"
import { Milestone, MilestonePreview } from "./Milestone"
import { ProjectPreview } from "./Project"

export type GoalStatus = "CURRENT" | "NEXT_IN_LINE" | "FUTURE" | "CLOSE" | "LATER"

/** Goal statuses that own a Roadmap column; the order is the column order on the page. */
export type RoadmapGoalStatus = "CURRENT" | "NEXT_IN_LINE" | "FUTURE"

export const ROADMAP_GOAL_STATUSES: RoadmapGoalStatus[] = ["CURRENT", "NEXT_IN_LINE", "FUTURE"]

/** Goal statuses shown in the All Goals table and its status filter. */
export type GoalTableStatus = RoadmapGoalStatus | "CLOSE"

export const GOAL_TABLE_STATUSES: GoalTableStatus[] = [...ROADMAP_GOAL_STATUSES, "CLOSE"]

export class Goal {
  id = ""
  status: GoalStatus = "NEXT_IN_LINE"
  content = ""
  important = ""
  whyNow = ""
  timeline = ""
  videoUrl = ""
  project?: ProjectPreview
  milestone?: MilestonePreview
  priority: number | null = null
  featuresCount = 0
  epicsCount = 0
  itemsCount = 0
  completedItemsCount = 0
  inProgressItemsCount = 0
  notStartedItemsCount = 0
  attachmentIds: string[] = []
  fileMetadata: FileMetaData[] = []
  features: Feature[] = []
  milestones: Milestone[] = []
  closedDate: Date | null = null
  startedDate: Date | null = null

  constructor(props?: Partial<Goal>) {
    if (!props) return

    Object.keys(props).forEach(field => {
      if (props[field] === undefined) return
      this[field] = props[field]
    })

    this.milestones = this.milestones.map(milestone => new Milestone(milestone))
    this.closedDate = this.closedDate ? toDate(this.closedDate) : null
    this.startedDate = this.startedDate ? toDate(this.startedDate) : null
    this.fileMetadata = this.fileMetadata.map(fm => new FileMetaData(fm))
    this.features = this.features.map(feature => Feature.create(feature))
  }

  static create(goal?: Partial<Goal>): Goal {
    return new Goal(goal)
  }

  get projectId(): string {
    return this.project?.id ?? ""
  }

  static createDefault(): Goal {
    return new Goal()
  }

  getJsonObj() {
    return {
      content: this.content,
      current: this.isCurrent,
      important: this.important,
      whyNow: this.whyNow,
      timeline: this.timeline,
      milestoneId: this.milestone?.id,
      projectId: this.project?.id,
      status: this.status,
      videoUrl: this.videoUrl,
      attachmentIds: this.attachmentIds
    }
  }

  get isCurrent() {
    return this.status === "CURRENT"
  }

  get isClosed() {
    return this.status === "CLOSE"
  }

  /** Milestones can be created for a Goal of any level except a closed one. */
  get canCreateMilestone() {
    return !this.isClosed
  }

  get isNextInLine() {
    return this.status === "NEXT_IN_LINE"
  }

  get isFuture() {
    return this.status === "FUTURE"
  }
}
